import { Dispatch, SetStateAction, useState, useMemo } from "react";
import PaginationUI from "../atoms/PaginationUI";
import ColumnText from "../molecules/ColumnText";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faTimes } from "@fortawesome/free-solid-svg-icons";
import { QRCodeSVG } from "qrcode.react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { toasts } from "@/shared/utils/toasts";

export type CampaignPageProps = {
  id: number;
  code: string;
  name: string;
  description: string;
  state: number;
  branch_id: number;
  created_at: string;
  updated_at: string;
  cost: number;
  sex: number;
  phone: number;
  comment: number;
  birthdate: number;
  media: string;
  media_check: number;
  clv: number;

  leads: string;
  leads_convert: string;
  cost_lead: string;
  profitability: string;
};

export type TableListCampaignProps = {
  campaigns: CampaignPageProps[];
  branch_id: string;
  setFormDataEdit: Dispatch<SetStateAction<CampaignPageProps>>;
};

const TableListCampaign: React.FC<TableListCampaignProps> = ({
  campaigns,
  setFormDataEdit,
  branch_id,
}) => {
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeURl, setQsCodeURL] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Calcular los elementos a mostrar en la página actual
  const paginatedCampaigns = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return campaigns?.slice(startIndex, endIndex) || [];
  }, [campaigns, currentPage, itemsPerPage]);

  // Calcular el total de páginas
  const totalPages = Math.ceil((campaigns?.length || 0) / itemsPerPage);

  const optionsMenu = [
    {
      name: "Codigo QR",
      functionMenu: (item) => {
        const link = `${window.location.origin}/campaign/register/user?code=${item.code}&media=${item.media}`;

        toasts.success("Éxito", "Código QR generado exitosamente");
        setQsCodeURL(link);
        setShowQRCode(true);
      },
    },
    {
      name: "LINK",
      functionMenu: async (item) => {
        try {
          if (!item.id) throw new Error();
          const link = `${window.location.origin}/campaign/register/user?code=${item.code}&media=${item.media}`;
          await window.navigator.clipboard.writeText(link);
          toasts.success("Éxito", "Código copiado correctamente");
        } catch (error) {
          toasts.danger("Error", "Error al copiar el link en portapapeles");
        }
      },
    },
  ];

  const handleEdit = (campaign: CampaignPageProps) => {
    setFormDataEdit(campaign);
  };

  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowQRCode(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-0">
      <div className="mt-2 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="max-h-svh overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-4 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter sm:pl-6 lg:pl-8"
                    >
                      Nombre
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Descripción
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Leads
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Leads convertidos
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Coste por Lead
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    >
                      Rentabilidad
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-semibold text-gray-900 backdrop-blur backdrop-filter"
                    ></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedCampaigns?.map((campaign) => {
                    const isLast = Boolean(
                      paginatedCampaigns[paginatedCampaigns.length - 1]
                    );
                    return (
                      <tr key={campaign.id}>
                        <ColumnText title={campaign.name} />
                        <ColumnText title={campaign.description} />
                        <ColumnText title={campaign.leads} />
                        <ColumnText title={campaign.leads_convert} />
                        <ColumnText title={campaign.cost_lead} />
                        <ColumnText title={campaign.profitability} />
                        <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0 flex">
                          <a
                            onClick={() => handleEdit?.(campaign)}
                            className="text-primary-600 hover:text-primary-900 mr-4 cursor-pointer"
                          >
                            Editar<span className="sr-only">, Editar</span>
                          </a>

                          <Menu as="div" className="relative flex-none">
                            <MenuButton className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                              <span className="sr-only">Open options</span>
                              <FontAwesomeIcon
                                icon={faEllipsisVertical}
                                aria-hidden="true"
                                className="h-6 w-6"
                              />
                            </MenuButton>
                            <MenuItems
                              transition
                              className={`absolute z-50 w-32 rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none
        ${isLast ? "bottom-full mb-2" : "mt-2 top-full"} md:right-0`}
                            >
                              {optionsMenu?.map((option, idx) => (
                                <MenuItem key={idx}>
                                  <a
                                    href="#"
                                    className="block px-3 py-1 text-sm leading-6 text-gray-900 data-[focus]:bg-gray-50 text-left"
                                    onClick={() =>
                                      option.functionMenu(campaign)
                                    }
                                  >
                                    {option.name}
                                    <span className="sr-only">
                                      , {option.name}
                                    </span>
                                  </a>
                                </MenuItem>
                              ))}
                            </MenuItems>
                          </Menu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {totalPages > 1 && (
        <PaginationUI
          currentPage={currentPage}
          totalPages={Math.ceil((campaigns.length ?? 0) / itemsPerPage)}
          onPageChange={(newPage) => handlePageChange(newPage)}
          pageSize={itemsPerPage}
          totalResults={campaigns.length}
        />
      )}

      {showQRCode && qrCodeURl && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOutsideClick}
        >
          <motion.div
            className="bg-white p-6 rounded-lg shadow-lg"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">QR Code</h3>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={() => setShowQRCode(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <QRCodeSVG value={qrCodeURl} size={200} />
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default TableListCampaign;
